import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input } from 'src/@vymo/ui/atoms';
import Text from 'src/@vymo/ui/atoms/text';
import { Body, Footer, Header, Modal } from 'src/@vymo/ui/blocks';
import Table from 'src/@vymo/ui/blocks/table';
import { ReactComponent as CopyIconSvg } from 'src/assets/icons/copy.svg';
import { Keys, locale } from 'src/i18n';
// eslint-disable-next-line vymo-ui/restrict-import
import { getRevyreModuleLabelByCode } from 'src/models/lmsConfig/queries';
import axios from 'src/workspace/axios';
import { getClientConfigData } from 'src/workspace/utils';
import { useFormContext } from '../../formProvider';
import { FormContextProps } from '../../formProvider/types';
import { referralOnclick } from '../referralDropdown/queries';
import { getColumns } from './queries';
import type { EntitySelectionProps } from './types';
import styles from './index.module.scss';

// eslint-disable-next-line complexity
function EntitySelection({
  code,
  value = '{}',
  onChange,
  workflowType,
  setReferralData,
  viewMode = false,
  returnCode = false,
  version,
  multi,
  disabled,
  filters,
  ...props
}: EntitySelectionProps &
  FormContextProps & { version: FormContextProps['config']['version'] }) {
  const online = true;
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [entityList, setEntityList] = useState<Record<string, any>>([]);
  const [selectedEntities, setSelectedEntities] = useState<
    Record<string, boolean>
  >({});
  const { getWorkflowConfig } = useFormContext(false);
  const workflowConfig = getWorkflowConfig();

  const [currentPageSize, setCurrentPageSize] = useState<number>(10);
  const [currentSelectedPage, setCurrentPage] = useState<number>(1);
  const [maxTablePages, setMaxPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>('');
  const isLan = workflowType === 'loan';

  let entityName;
  let entityCode;

  try {
    entityCode = JSON.parse(value)?.[0]?.code;
    entityName = JSON.parse(value)?.[0]
      ? JSON.parse(value)?.[0]?.name ||
        `${locale(Keys.NO_NAME)} (${entityCode})`
      : '';
  } catch (error) {
    if (returnCode) {
      entityCode = value;
      const selectedEntity = entityList?.find(
        (entity) => entity.id === entityCode,
      );
      entityName = selectedEntity ? selectedEntity?.name || 'undefined' : '';
    } else {
      entityCode = value;
      const selectedEntity = isLan
        ? entityList?.find((entity) => entity?.lan === entityCode)
        : entityList?.find((entity) => entity?.id === entityCode);

      entityName = isLan ? selectedEntity?.lan : selectedEntity?.name;
    }
  }

  const columns = getColumns(workflowConfig[workflowType] || {});

  useMemo(() => {
    if (entityList?.length > 0 && entityCode) {
      const inputsMap = entityList?.find((option: any) =>
        isLan ? option.lan === entityCode : option.id === entityCode,
      );
      setReferralData({ fieldCode: code, data: inputsMap });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, entityCode, entityList, setReferralData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchEntities = useCallback(
    _.debounce(async (payload) => {
      try {
        setLoading(true);
        const response = await axios.post(
          `/vymo/v3/workflow/${workflowType}/list`,
          { ...payload, filters },
        );
        const { result: workflowList = [], totalCount: responseTotalCount } =
          response?.data?.payload ?? {};
        const entities = workflowList.map(
          (listItem) => listItem?.mutatedEntity,
        );

        setEntityList(entities);
        setMaxPages(Math.ceil(responseTotalCount / currentPageSize));
        setTotalCount(responseTotalCount);
      } catch (error) {
        // console.log(error);
      } finally {
        setLoading(false);
      }
    }, 500),
    [workflowType],
  );

  const fetchEntityDetails = useCallback(
    async (workflowCode, moduleCode) => {
      try {
        setLoading(true);
        const response = await axios.get(
          `/vymo/v3/workflow/${moduleCode}/${workflowCode}`,
        );
        const mutatedEntity = response?.data?.payload?.mutatedEntity;
        setEntityList([mutatedEntity]);
        setReferralData({ fieldCode: code, data: mutatedEntity });
      } catch (e) {
        // console.log(error)
      } finally {
        setLoading(false);
      }
    },
    [code, setReferralData],
  );

  useEffect(() => {
    if (disabled) {
      if (entityCode) {
        fetchEntityDetails(entityCode, workflowType);
      }
    } else if (!viewMode) {
      const payload: any = {
        page: {
          pageNumber: currentSelectedPage,
          pageSize: currentPageSize,
        },
      };
      if (searchText) {
        payload.searchText = searchText;
      }
      fetchEntities(payload);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPageSize,
    currentSelectedPage,
    online,
    workflowType,
    searchText,
    viewMode,
    disabled,
  ]);

  useEffect(() => {
    if (entityCode) {
      const initialSelections: Record<string, boolean> = {};
      entityList?.forEach((entity) => {
        if (entity?.id === entityCode) {
          initialSelections[entity?.id] = true;
        }
      });
      setSelectedEntities(initialSelections);
    }
  }, [entityCode, entityList]);

  const onClickInput = () => {
    setShowModal(true);
  };

  const onEntitySelection = useCallback(
    (__, selectedRows) => {
      setSelectedEntities(selectedRows);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(selectedEntities)],
  );

  const onSubmit = () => {
    const entityCodes = Object.keys(selectedEntities).filter(
      (key) => selectedEntities[key],
    );
    const selectedEntityItems = (entityList || []).filter((entity) =>
      entityCodes?.includes(entity?.id),
    );

    const mappedSelectedEntity = selectedEntityItems.map((entity) => ({
      code: entity?.id,
      name: entity?.name,
    }));

    if (returnCode) {
      onChange(mappedSelectedEntity?.[0]?.code, {} as React.ChangeEvent<any>);
    } else {
      onChange(
        JSON.stringify(mappedSelectedEntity),
        {} as React.ChangeEvent<any>,
      );
    }
    setReferralData({ fieldCode: code, data: selectedEntityItems?.[0] });
    setShowModal(false);
  };

  const workflowName = getRevyreModuleLabelByCode(workflowType);

  return !viewMode ? (
    <>
      <Input
        value={entityName}
        onClick={!disabled ? onClickInput : undefined}
        onChange={(inputValue) => {
          if (inputValue === '') {
            setSelectedEntities({});
            onChange(
              returnCode ? '' : JSON.stringify([]),
              {} as React.ChangeEvent<any>,
            );
          }
        }}
        {..._.omit(props, ['label'])}
        iconRight={<CopyIconSvg />}
        disabled={disabled}
        loading={loading}
        showDisabledIcon={!loading}
      />
      {showModal && (
        <Modal
          classNames={styles.entitySelectionModal}
          onClose={() => {
            setShowModal(false);
          }}
        >
          <Header>
            <Text bold type="h4">
              {`Select ${workflowName}`}
            </Text>
          </Header>
          <Body>
            <Table
              isAccordion={false}
              tableData={entityList}
              columnConfigs={columns ?? []}
              onSearch={(searchTerm) => setSearchText(searchTerm)}
              tableConfig={{
                showSearch: true,
                isRowSelectionEnabled: true,
                singleRowSelection: !multi,
                name: '',
                hideColumnConfigurator: true,
                pagination: online
                  ? {
                      visible: true,
                      pageSize: currentPageSize,
                      totalPages: maxTablePages,
                      currentPage: currentSelectedPage,
                      totalItems: totalCount,
                    }
                  : {
                      visible: true,
                    },
                selections: selectedEntities,
              }}
              onRowSelect={onEntitySelection}
              onChangePageIndex={(pageIndex) => {
                setCurrentPage(Number(pageIndex) + 1);
              }}
              onChangePageSize={(pageSize) => {
                setCurrentPageSize(pageSize);
                setCurrentPage(1);
              }}
            />
          </Body>
          <Footer>
            <Button onClick={onSubmit} data-test-id="entity-selection-submit">
              Submit
            </Button>
          </Footer>
        </Modal>
      )}
    </>
  ) : (
    <Button
      variant="subtle"
      type="primary"
      size="small"
      onClick={() =>
        referralOnclick(
          version,
          workflowType,
          entityCode,
          getClientConfigData(),
        )
      }
    >
      {entityName}
    </Button>
  );
}

export default EntitySelection;
